import React, { useState } from 'react';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';

import { AUTH_DISPLAY_FONT, AUTH_LINK_COLOR, authPrimaryButtonSx, Eyebrow } from './AuthLayout';

const CODE_LENGTH = 6;

/** The single code field: the sign-in field look, with the digits centred and lightly spaced. */
const codeFieldSx = {
  '& .MuiOutlinedInput-root': {
    height: '48px',
    backgroundColor: '#F6F8FB',
    borderRadius: '10px',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#DCE3ED' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#B9C4D2' },
    '&.Mui-focused': { boxShadow: '0 0 0 4px rgba(5,189,255,0.16)' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(5,189,255,0.7)', borderWidth: '1px' },
  },
  '& .MuiOutlinedInput-input': {
    height: 'auto',
    padding: '0 16px',
    lineHeight: 1.2,
    textAlign: 'center',
    fontSize: '16px',
    fontWeight: 600,
    letterSpacing: '0.2em',
    color: '#091115',
    // Same size as the placeholder, so the text stays centred whether the field is empty or not.
    '&:placeholder-shown': { fontWeight: 400, letterSpacing: 0 },
    '&::placeholder': { color: '#8796AA', opacity: 1 },
  },
};

/**
 * The verification-code step (SMS or authenticator app), styled as sign-in card content
 * (SKD-2379): eyebrow, Manrope heading, ONE code field (SKD-2380 - a single box so phones can
 * drop the code straight in from the SMS; it submits by itself on the sixth digit, or via the
 * button), the explanation, then the resend link. It renders no frame of its own - put it inside
 * AuthLayout's card or a dialog.
 */
export default function SmsCode({
  eyebrow = 'Two-step verification',
  title,
  subtitle,
  footer = '',
  phone = '',
  loading,
  actions,
  showResend = true,
}: {
  eyebrow?: string;
  title: string;
  subtitle: string;
  footer?: string;
  phone?: string;
  loading: boolean;
  actions: { resend?: () => void; signIn: (code: string) => void };
  showResend?: boolean;
}) {
  const [code, setCode] = useState('');

  function handleChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const digits = event.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH);
    setCode(digits);
    if (digits.length === CODE_LENGTH && !loading) {
      actions.signIn(digits);
    }
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (code.length === CODE_LENGTH && !loading) {
      actions.signIn(code);
    }
  }

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Eyebrow>{eyebrow}</Eyebrow>
      <Typography
        component="h1"
        sx={{
          fontFamily: AUTH_DISPLAY_FONT,
          fontSize: { xs: '28px', sm: '32px' },
          lineHeight: 1.08,
          letterSpacing: '-0.028em',
          fontWeight: 700,
          color: '#091115',
          mb: 1.5,
        }}>
        {title}
      </Typography>
      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
        {subtitle}
      </Typography>
      {phone !== '' && (
        <Typography variant="body1" sx={{ color: '#091115', fontWeight: 600, mt: 0.75 }}>
          {phone}
        </Typography>
      )}

      <Box component="form" noValidate onSubmit={handleSubmit} sx={{ my: 4 }}>
        <TextField
          id="code"
          name="code"
          value={code}
          onChange={handleChange}
          disabled={loading}
          autoFocus
          fullWidth
          placeholder="Enter the 6-digit code"
          inputProps={{
            inputMode: 'numeric',
            pattern: '[0-9]*',
            maxLength: CODE_LENGTH,
            // Lets iOS and Android offer the code from the SMS (or the authenticator app) above
            // the keyboard and fill the field in one tap.
            autoComplete: 'one-time-code',
            'aria-label': 'Verification code',
          }}
          sx={codeFieldSx}
        />
        <Box sx={{ mt: 2.25 }}>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading || code.length !== CODE_LENGTH}
            sx={authPrimaryButtonSx}>
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify'}
          </Button>
        </Box>
      </Box>

      {showResend && actions.resend != null && (
        <Box sx={{ borderTop: '1px solid #EEF2F7', pt: 3 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Haven&apos;t received your code?{' '}
            <Button
              variant="text"
              onClick={actions.resend}
              disabled={loading}
              sx={{
                p: 0,
                minWidth: 0,
                minHeight: 0,
                verticalAlign: 'baseline',
                fontSize: 'inherit',
                fontWeight: 600,
                color: AUTH_LINK_COLOR,
                '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
                '@media (max-width:899.95px)': { minHeight: 0 },
              }}>
              Resend my code
            </Button>
          </Typography>
        </Box>
      )}

      {footer !== '' && (
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
          {footer}
        </Typography>
      )}
    </Box>
  );
}
